/** @sfdc-extension-file SNK */
'use client';

import { useEffect, useState } from 'react';

/** `top` = full header, `hidden` = scrolled down, `condensed` = scrolled back up. */
export type HeaderScrollState = 'top' | 'hidden' | 'condensed';

type UseScrollDirectionOptions = {
    /** Scroll offset (px) above which `top` is left. */
    threshold?: number;
    /** Re-entry point for `top`. Anchoring adds the header's growth to scrollY, so keep it clear of `threshold`. */
    expandThreshold?: number;
    /** Minimum scroll delta (px) before a direction change is honoured. Stops jitter. */
    hysteresis?: number;
};

const DEFAULT_THRESHOLD_PX = 120;
const DEFAULT_EXPAND_THRESHOLD_PX = 40;
const DEFAULT_HYSTERESIS_PX = 5;

/** rAF-throttled, with hysteresis so trackpad jitter doesn't flip the header. */
export function useScrollDirection({
    threshold = DEFAULT_THRESHOLD_PX,
    expandThreshold = DEFAULT_EXPAND_THRESHOLD_PX,
    hysteresis = DEFAULT_HYSTERESIS_PX,
}: UseScrollDirectionOptions = {}): HeaderScrollState {
    // SSR and first paint render the default header — the page is at the top on load.
    const [state, setState] = useState<HeaderScrollState>('top');

    useEffect(() => {
        let lastScrollY = window.scrollY;
        let frame: number | null = null;
        let isFirstMeasure = true;
        let currentState: HeaderScrollState = 'top';

        const apply = (next: HeaderScrollState) => {
            currentState = next;
            setState(next);
        };

        const measure = () => {
            frame = null;
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - lastScrollY;
            const wasFirstMeasure = isFirstMeasure;
            isFirstMeasure = false;

            if (currentScrollY <= (currentState === 'top' ? threshold : expandThreshold)) {
                lastScrollY = currentScrollY;
                apply('top');
                return;
            }

            // Restored scroll position: no direction to read, so show condensed.
            if (wasFirstMeasure) {
                lastScrollY = currentScrollY;
                apply('condensed');
                return;
            }

            if (Math.abs(delta) < hysteresis) {
                return;
            }

            lastScrollY = currentScrollY;
            apply(delta > 0 ? 'hidden' : 'condensed');
        };

        const handleScroll = () => {
            if (frame !== null) {
                return;
            }
            frame = window.requestAnimationFrame(measure);
        };

        measure();

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [expandThreshold, hysteresis, threshold]);

    return state;
}
